// =============================================================================
// src/views/demografica/demografica-sync.ts
//
// Sincronizacao do recorte: chips selecionados -> tags ativas, KPIs, narrativa
// dinamica e re-estilizacao do mapa. Equivalente fiel a Sections.demografica.
// _syncRecorte / _applyStyles (assets/unified-app.js linhas 1343-1383).
//
// O modulo NAO usa singleton: recebe o MapController por argumento (DI) e mantem
// o recorte corrente num pequeno estado encapsulado (DemograficaRecorteState),
// substituindo o `this.currentRecorte` mutavel do objeto original.
//
// Regra Clean Code: funcoes 4-20 linhas, early-return, zero `any`, nomes
//   greppaveis. Tudo que toca o DOM/estilo do mapa vive aqui (SRP).
// =============================================================================

// PathOptions vem direto do @types/leaflet (DefinitelyTyped). types.ts importa
// mas nao reexporta; o tipo de retorno da style fn e a superficie publica.
// Verificado via Context7 (/leaflet/leaflet) em 2026-05-30: fillColor,
// fillOpacity, weight, color, opacity sao opcoes validas de Path.
import type { PathOptions } from 'leaflet';
import { formatPeopleCompactPt } from '../../app/format-ptbr';
import type { MapController, ZonaKey } from '../../app/types';
import { MANAUS_ELECTORAL_BY_YEAR } from '../../data';
import {
  DEMOGRAFICA_CONCENTRACAO,
  DEMOGRAFICA_CONCENTRACAO_FALLBACK,
  DEMOGRAFICA_RECORTE_DEFAULT,
  DEMOGRAFICA_RECORTE_POR_GRUPO,
  type DemograficaGrupoChip,
  type DemograficaRecorte,
} from './demografica-recorte-data';

/** Rotulo de um chip selecionado: grupo (data-g) + texto exibido. */
interface DemograficaTagLabel {
  readonly group: string;
  readonly text: string;
}

/**
 * Estado encapsulado do recorte corrente. Substitui `Sections.demografica.
 * currentRecorte`. Mutavel por design — espelha o comportamento original.
 */
export interface DemograficaRecorteState {
  currentRecorte: DemograficaRecorte;
}

/** Cria o estado inicial do recorte (default 'renda', como no original). */
export function createDemograficaRecorteState(): DemograficaRecorteState {
  return { currentRecorte: DEMOGRAFICA_RECORTE_DEFAULT };
}

/** Equivalente a UI.qsa: array a partir de querySelectorAll num escopo. */
function querySelectorAllArray(selector: string, scope: ParentNode): Element[] {
  return Array.prototype.slice.call(scope.querySelectorAll(selector));
}

/** Le os chips selecionados da secao e devolve seus rotulos (grupo + texto). */
function readSelectedTagLabels(section: HTMLElement): DemograficaTagLabel[] {
  const selected = querySelectorAllArray('.u-filter-group .u-chip.selected[data-g]', section);
  return selected.map((chip) => ({
    /* v8 ignore next -- seletor [data-g] garante dataset.g presente; ?? '' defensivo p/ noUncheckedIndexedAccess */
    group: (chip as HTMLElement).dataset.g ?? '',
    /* v8 ignore next -- textContent de Element nunca é null; ?? '' defensivo CLAUDE.md rule 11 */
    text: (chip.textContent ?? '').trim(),
  }));
}

/** Monta o HTML das tags ativas (ou o placeholder "Sem filtros ativos"). */
function buildActiveTagsHtml(labels: readonly DemograficaTagLabel[]): string {
  if (labels.length === 0) {
    return '<span style="font-size:10px;color:var(--color-text-secondary)">Sem filtros ativos</span>';
  }
  return labels
    .map(
      (tagLabel) =>
        '<span class="u-recorte-chip" data-g="' +
        tagLabel.group +
        '">' +
        tagLabel.text +
        ' <span class="rm">×</span></span>',
    )
    .join('');
}

/**
 * Deriva o recorte corrente a partir do ULTIMO chip selecionado. Sem chips,
 * cai em 'income' -> 'renda', exatamente como o original (linhas 1356-1358).
 */
function resolveCurrentRecorte(labels: readonly DemograficaTagLabel[]): DemograficaRecorte {
  const lastLabel = labels.length > 0 ? labels[labels.length - 1] : undefined;
  const lastGroup = (lastLabel?.group ?? 'income') as DemograficaGrupoChip;
  return DEMOGRAFICA_RECORTE_POR_GRUPO[lastGroup] ?? DEMOGRAFICA_RECORTE_DEFAULT;
}

/**
 * Calcula a contagem do recorte em milhares. Ancorada no recorte default
 * (3 filtros = 84k eleitoras, vindo da tela-06). Cada filtro adicional retira
 * ~14k; cada filtro removido amplia o recorte. Mantem coerencia com a "amostra
 * 84k" exibida. Proveniencia: assets/unified-app.js linhas 1362-1365.
 */
function computeRecorteCount(selectedTagCount: number): number {
  return Math.max(14, 84 + (3 - selectedTagCount) * 14);
}

/** Texto narrativo do feedback dinamico (vazio quando nao ha filtros). */
function buildInsightNarrative(labels: readonly DemograficaTagLabel[]): string {
  if (labels.length === 0) return '';
  const recorte = labels.map((tagLabel) => tagLabel.text).join(' + ');
  return (
    'Recorte atual: ' +
    recorte +
    '. Concentração acima da média em Centro-Sul e Sul; voto Avante ainda competitivo e PT acima da base municipal.'
  );
}

/** Escreve KPIs (total, share) e a narrativa nos respectivos nos do DOM. */
function writeKpisAndNarrative(labels: readonly DemograficaTagLabel[]): void {
  const count = computeRecorteCount(labels.length);
  const total = document.getElementById('u-demo-total');
  const share = document.getElementById('u-demo-share');
  const insight = document.getElementById('u-demo-insight');
  // Reforma 2026-06 (D3): contador computado formatado em pt-BR ('98 mil'),
  // nunca '98k'. count chega em milhares -> converte p/ unidades.
  if (total) total.textContent = formatPeopleCompactPt(count * 1000);
  if (share) share.textContent = (count / 14.2).toFixed(1).replace('.', ',') + '%';
  if (insight) insight.textContent = buildInsightNarrative(labels);
}

/**
 * Estilo de zona do recorte: cor base do voto 2024 com opacidade modulada pela
 * concentracao do recorte corrente. Proveniencia: linhas 1378-1382 (intacto).
 */
function buildRecorteZoneStyle(
  state: DemograficaRecorteState,
  zonaKey: ZonaKey,
): PathOptions {
  // `?? FALLBACK` cobre tanto recorte quanto zona ausentes — replica o `|| 0.5`
  // do original (linha 1379) e satisfaz noUncheckedIndexedAccess.
  const concentration =
    DEMOGRAFICA_CONCENTRACAO[state.currentRecorte]?.[zonaKey] ??
    DEMOGRAFICA_CONCENTRACAO_FALLBACK;
  // Todas as 6 zonas existem em electoral['2024']; o resultado [1] e a cor base.
  const baseColor = MANAUS_ELECTORAL_BY_YEAR['2024'][zonaKey]?.[1];
  return {
    fillColor: baseColor,
    fillOpacity: 0.2 + concentration * 0.6,
    weight: 0.8,
    color: 'rgba(255,255,255,0.5)',
    opacity: 1,
  };
}

/** Aplica os estilos de recorte ao mapa (Sections.demografica._applyStyles). */
export function applyDemograficaStyles(
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  mapCtrl.setZoneStyles((zonaKey) => buildRecorteZoneStyle(state, zonaKey));
}

/**
 * Sincroniza tags ativas, KPIs, narrativa e estilos do mapa a partir dos chips
 * selecionados. Equivalente fiel a Sections.demografica._syncRecorte.
 *
 * @example syncDemograficaRecorte(state, MapControllerInstance);
 */
export function syncDemograficaRecorte(
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  const section = document.getElementById('section-demografica');
  if (!section) return;
  const labels = readSelectedTagLabels(section as HTMLElement);
  const active = document.getElementById('u-demo-active-tags');
  if (active) active.innerHTML = buildActiveTagsHtml(labels);
  state.currentRecorte = resolveCurrentRecorte(labels);
  writeKpisAndNarrative(labels);
  applyDemograficaStyles(state, mapCtrl);
}
